import { createFileRoute } from "@tanstack/react-router";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

export const Route = createFileRoute("/privacy")({
  component: Privacy,
});

function Privacy() {
  return (
    <>
      <Header />
      <div className="legal-wrap">
        <main className="docs-content">
          <h1>Privacy</h1>
          <p className="legal-date">Last updated: June 21, 2026</p>
          <p className="lead">
            notify is notification infrastructure. We hold the least we can to deliver a notification,
            and nothing we don’t need to. This policy explains exactly what that is.
          </p>

          <h2>Overview</h2>
          <p>
            You — the developer — embed notify and tell us which of <em>your</em> users to notify.
            notify stores the device subscription behind an identifier you choose and delivers the
            message. We are a processor acting on your instructions; you decide who gets notified and
            what they’re told.
          </p>

          <h2>What we collect</h2>
          <ul>
            <li>
              <strong>Push subscriptions</strong> — the browser push endpoint and its public keys
              (<span className="icode">p256dh</span>, <span className="icode">auth</span>), created by
              the user’s browser when they opt in.
            </li>
            <li>
              <strong>The user identifier you send</strong> — the <span className="icode">userId</span>{" "}
              you pass to <span className="icode">notify.subscribe()</span>. It is opaque to us. Send a
              pseudonymous id, not an email or name.
            </li>
            <li>
              <strong>API tokens</strong> — the tokens you create to authenticate requests.
            </li>
            <li>
              <strong>Operational data</strong> — IP addresses, timestamps, and request metadata, used
              only for rate limiting, abuse prevention, and debugging. Your raw IP is used in memory to
              enforce rate limits and is never written to our telemetry: what we store is a salted,
              irreversible hash of it plus the country, which lets us spot abuse without keeping an
              address that points back to a person.
            </li>
          </ul>

          <h2>What we don’t do</h2>
          <ul>
            <li>We don’t require an account to start.</li>
            <li>We don’t sell personal information — ever.</li>
            <li>We don’t track your end users across sites or build advertising profiles.</li>
            <li>
              We don’t retain notification content. Titles and bodies are encrypted and forwarded to
              the browser’s push service for delivery, then dropped.
            </li>
          </ul>

          <h2>How we use data</h2>
          <p>
            Strictly to run the service: map a <span className="icode">userId</span> to its devices,
            deliver notifications, enforce rate limits, prevent abuse, and keep enough records to debug
            delivery. Nothing here is used for marketing.
          </p>

          <h2>Who we share it with</h2>
          <ul>
            <li>
              <strong>Browser push services</strong> — to deliver a notification, the encrypted payload
              is relayed through the user’s browser vendor (Google, Mozilla, Apple, or Microsoft). This
              is how the Web Push standard works.
            </li>
            <li>
              <strong>Infrastructure providers</strong> — the service runs on Cloudflare, which
              processes requests on our behalf.
            </li>
            <li>
              <strong>Observability provider</strong> — we send operational traces to Axiom to monitor
              performance and debug failures. A trace records timing, the endpoint called, the outcome,
              the hashed client identifier and country described above, and the{" "}
              <span className="icode">userId</span> involved. It never contains notification content or
              a raw IP address.
            </li>
          </ul>
          <p>That’s the entire list. No data brokers, no ad networks.</p>

          <h2>Retention</h2>
          <ul>
            <li>Subscriptions: kept until the user unsubscribes or you delete the associated token.</li>
            <li>Tokens: kept until you delete them.</li>
            <li>
              Operational traces (timing, endpoint, outcome, hashed client id, country): 30 days, then
              deleted.
            </li>
            <li>Notification content: not retained after delivery.</li>
          </ul>

          <h2>New channels</h2>
          <p>
            notify starts with browser push and will add channels over time (in-app, email, and more).
            Each new channel follows the same rule — collect only what’s required to deliver — and this
            policy will be updated before a channel handles new categories of data.
          </p>

          <h2>Your controls</h2>
          <p>
            Deleting a token removes the subscriptions tied to it. End users can revoke notifications at
            any time in their browser, which invalidates the subscription. You can also run notify on
            your own Cloudflare account, in which case all data stays in your infrastructure.
          </p>

          <h2>Changes</h2>
          <p>We’ll post updates here with a new date. Material changes will be called out.</p>

          <h2>Contact</h2>
          <p>
            Questions or a deletion request? Email{" "}
            <a className="ilink" href="mailto:contact@getnotify.dev">
              contact@getnotify.dev
            </a>
            .
          </p>
        </main>
      </div>
      <Footer />
    </>
  );
}
